import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { createQuotationAttempt } from '../../utils/quotationCreateAttempt';

export const fetchQuotations = createAsyncThunk('quotations/fetchAll', async (params = {}, { rejectWithValue }) => {
  try {
          const response = await axios.get('/api/quotations', { params });
          return response.data;
    
  } catch (err) {
    // The response body IS the message. Without this, createAsyncThunk
    // keeps only err.message — literally "Request failed with status
    // code 400" — and the reason the server sent is thrown away.
    const body = err.response?.data;
    // Only an OBJECT body is a server explanation. nginx answers a 502 with
    // an HTML PAGE, which is truthy — passing it through made the reducer
    // store RTK's literal "Rejected" (its action.error.message for any
    // rejectWithValue), which reads worse than the status line it replaced.
    return rejectWithValue(
      body && typeof body === 'object' ? body : { error: err.message }
    );
  }
}
);

export const fetchQuotation = createAsyncThunk('quotations/fetchOne', async (id, { rejectWithValue }) => {
  try {
          const response = await axios.get(`/api/quotations/${id}`);
          return response.data;
    
  } catch (err) {
    // The response body IS the message. Without this, createAsyncThunk
    // keeps only err.message — literally "Request failed with status
    // code 400" — and the reason the server sent is thrown away.
    const body = err.response?.data;
    // Only an OBJECT body is a server explanation. nginx answers a 502 with
    // an HTML PAGE, which is truthy — passing it through made the reducer
    // store RTK's literal "Rejected" (its action.error.message for any
    // rejectWithValue), which reads worse than the status line it replaced.
    return rejectWithValue(
      body && typeof body === 'object' ? body : { error: err.message }
    );
  }
}
);

export const createQuotation = createAsyncThunk('quotations/create', async ({ data, attempt }, { rejectWithValue }) => {
  try {
          const response = await (attempt || createQuotationAttempt()).post(data);
          return response.data;
    
  } catch (err) {
    // The response body IS the message. Without this, createAsyncThunk
    // keeps only err.message — literally "Request failed with status
    // code 400" — and the reason the server sent is thrown away.
    const body = err.response?.data;
    // Only an OBJECT body is a server explanation. nginx answers a 502 with
    // an HTML PAGE, which is truthy — passing it through made the reducer
    // store RTK's literal "Rejected" (its action.error.message for any
    // rejectWithValue), which reads worse than the status line it replaced.
    return rejectWithValue(
      body && typeof body === 'object' ? body : { error: err.message }
    );
  }
}
);

export const updateQuotation = createAsyncThunk('quotations/update', async ({ id, data }, { rejectWithValue }) => {
  try {
          const response = await axios.put(`/api/quotations/${id}`, data);
          return response.data;
    
  } catch (err) {
    // The response body IS the message. Without this, createAsyncThunk
    // keeps only err.message — literally "Request failed with status
    // code 400" — and the reason the server sent is thrown away.
    const body = err.response?.data;
    // Only an OBJECT body is a server explanation. nginx answers a 502 with
    // an HTML PAGE, which is truthy — passing it through made the reducer
    // store RTK's literal "Rejected" (its action.error.message for any
    // rejectWithValue), which reads worse than the status line it replaced.
    return rejectWithValue(
      body && typeof body === 'object' ? body : { error: err.message }
    );
  }
}
);

export const resendQuotation = createAsyncThunk('quotations/resend', async (id, { rejectWithValue }) => {
  try {
          const response = await axios.post(`/api/quotations/${id}/resend`);
          return response.data;
    
  } catch (err) {
    // The response body IS the message. Without this, createAsyncThunk
    // keeps only err.message — literally "Request failed with status
    // code 400" — and the reason the server sent is thrown away.
    const body = err.response?.data;
    // Only an OBJECT body is a server explanation. nginx answers a 502 with
    // an HTML PAGE, which is truthy — passing it through made the reducer
    // store RTK's literal "Rejected" (its action.error.message for any
    // rejectWithValue), which reads worse than the status line it replaced.
    return rejectWithValue(
      body && typeof body === 'object' ? body : { error: err.message }
    );
  }
}
);

const quotationSlice = createSlice({
    name: 'quotations',
    initialState: {
        items: [],
        currentQuotation: null,
        loading: false,
        error: null,
        pagination: {
            total: 0,
            page: 1,
            pages: 1,
        },
    },
    reducers: {
        clearCurrentQuotation: (state) => {
            state.currentQuotation = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch all quotations
            .addCase(fetchQuotations.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchQuotations.fulfilled, (state, action) => {
                state.loading = false;
                state.items = action.payload.data;
                state.pagination = action.payload.pagination;
            })
            .addCase(fetchQuotations.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.error || action.payload?.message || action.error.message;
            })
            // Fetch single quotation
            .addCase(fetchQuotation.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchQuotation.fulfilled, (state, action) => {
                state.loading = false;
                state.currentQuotation = action.payload.data;
            })
            .addCase(fetchQuotation.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.error || action.payload?.message || action.error.message;
            })
            // Create quotation
            .addCase(createQuotation.fulfilled, (state, action) => {
                state.items.unshift(action.payload.data);
            })
            // Update quotation
            .addCase(updateQuotation.fulfilled, (state, action) => {
                const index = state.items.findIndex(q => q.id === action.payload.data.id);
                if (index !== -1) {
                    state.items[index] = action.payload.data;
                }
                state.currentQuotation = action.payload.data;
            });
    },
});

export const { clearCurrentQuotation } = quotationSlice.actions;
export default quotationSlice.reducer;
